import { api } from './api';

/**
 * Saving a file the API produced.
 *
 * The export endpoints are authenticated and the access token lives in memory,
 * not in a cookie, so a plain `<a href download>` to one of them simply 401s —
 * and does it silently, as an empty file or a login page saved as a CSV. The
 * file is therefore fetched like any other API call and handed to the browser
 * as a blob.
 */
export async function downloadFile(path: string, filename: string): Promise<void> {
  const { data } = await api.get<Blob>(path, { responseType: 'blob' });
  const url = URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.download = safeFilename(filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * The same, for a file the API builds from something too long for a web
 * address — the ticked rows of a list, say, which can run to hundreds of ids.
 */
export async function downloadPost(path: string, body: unknown, filename: string): Promise<void> {
  const { data } = await api.post<Blob>(path, body, { responseType: 'blob' });
  const url = URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.download = safeFilename(filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * A name a file system will take.
 *
 * Centre names and session names are typed by people and arrive with slashes,
 * colons and apostrophes in them; a download named "Saturday: 9am" saves as
 * something unpredictable, or nothing at all.
 */
export function safeFilename(name: string): string {
  const cleaned = name
    .replace(/[^\w.\- ]+/g, '-')
    .replace(/\s+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^[-.]+|[-.]+$/g, '');
  return cleaned || 'download';
}
